import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2, Mail, Info, X, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { AdminTabs } from "../../components/admin/AdminTabs";
import { useConfirm } from "../../components/ConfirmProvider";

export default function AdminDeletionRequests() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "es";
  const confirm = useConfirm();
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deployed, setDeployed] = useState(true);

  const load = () => api.get("/admin/deletion-requests")
    .then(({ data }) => setReqs(Array.isArray(data) ? data : []))
    .catch((err) => { if ([404, 405].includes(err?.response?.status)) setDeployed(false); })
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const deleteAccount = async (r) => {
    if (!(await confirm({
      title: t("admin.deletionRequests.deleteTitle"),
      description: t("admin.deletionRequests.deleteConfirm", { email: r.email }),
      confirmText: t("admin.deletionRequests.deleteBtn"),
    }))) return;
    try {
      await api.post(`/admin/deletion-requests/${r.id}/delete-account`);
      toast.success(t("admin.deletionRequests.deleted"));
      load();
    } catch (err) {
      if ([404, 405].includes(err?.response?.status)) toast.error(t("admin.deletionRequests.notDeployed"));
      else toast.error(t("common.error"));
    }
  };

  const dismiss = async (r) => {
    try { await api.post(`/admin/deletion-requests/${r.id}/dismiss`); load(); }
    catch (err) { if ([404, 405].includes(err?.response?.status)) toast.error(t("admin.deletionRequests.notDeployed")); else toast.error(t("common.error")); }
  };

  const badge = (status) => {
    const map = { pending: "bg-amber-100 text-amber-700", resolved: "bg-housing/15 text-housing", dismissed: "bg-stone-bg text-stone-muted" };
    return map[status] || map.pending;
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center"><ShieldAlert className="w-5 h-5 text-red-600" /></div>
        <h1 className="font-heading text-3xl font-bold text-stone-text">{t("admin.deletionRequests.title")}</h1>
      </div>
      <p className="text-stone-sub mb-6">{t("admin.deletionRequests.subtitle")}</p>
      <AdminTabs />

      {!deployed && (
        <div className="flex items-start gap-3 bg-brand-soft border border-stone-border rounded-2xl p-4 mb-6" data-testid="deletion-not-deployed">
          <Info className="w-5 h-5 text-brand shrink-0 mt-0.5" />
          <p className="text-sm text-stone-sub">{t("admin.deletionRequests.notDeployedInfo")}</p>
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-stone-muted">{t("jobs.loading")}</div>
      ) : reqs.length === 0 ? (
        <div className="py-16 text-center text-stone-muted bg-white rounded-2xl border border-stone-border" data-testid="deletion-empty">
          <Mail className="w-10 h-10 mx-auto mb-3 text-stone-muted/60" />
          {t("admin.deletionRequests.empty")}
        </div>
      ) : (
        <div className="space-y-3" data-testid="deletion-list">
          {reqs.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-stone-border shadow-sm p-4 flex items-start justify-between gap-4 flex-wrap" data-testid="deletion-row">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-heading font-semibold text-stone-text">{r.email}</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badge(r.status)}`}>{t(`admin.deletionRequests.status_${r.status}`, r.status)}</span>
                </div>
                {r.reason && <p className="text-sm text-stone-sub mt-1">{r.reason}</p>}
                {r.created_at && <p className="text-xs text-stone-muted mt-1">{new Date(r.created_at).toLocaleString(lang === "en" ? "en-US" : "es-MX")}</p>}
              </div>
              {r.status === "pending" && (
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid="deletion-dismiss" onClick={() => dismiss(r)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-border text-stone-sub hover:text-stone-text transition-colors">
                    <X className="w-4 h-4" /> {t("admin.deletionRequests.dismiss")}
                  </button>
                  <button data-testid="deletion-delete-account" onClick={() => deleteAccount(r)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors">
                    <Trash2 className="w-4 h-4" /> {t("admin.deletionRequests.deleteBtn")}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
